import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_elite-fitness-jorge/artifacts/6jxycqhh_01_hero_playa.jpg";
const HERO_ALT = "https://customer-assets.emergentagent.com/job_elite-fitness-jorge/artifacts/wprcq7wv_04_hero_alt_playa_serio.jpg";

export default function Hero({ onWatchStory }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const scrollNext = () => {
    const el = document.getElementById("stats");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen w-full overflow-hidden grain-overlay"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        style={isMobile ? {} : { y }}
        className="absolute inset-0 -top-20 -bottom-20"
      >
        <img
          src={HERO_IMG}
          alt="Jorge Masterfit · Playa del Carmen"
          className="absolute inset-0 w-full h-full object-cover object-[center_top] sm:object-[70%_20%] lg:object-[75%_25%]"
          loading="eager"
        />
        {/* Dark overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/20" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={isMobile ? {} : { opacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-20 sm:pb-28 pt-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.7 }}
          className="text-[10px] sm:text-xs tracking-[0.5em] text-[#FF6B00] mb-6 uppercase"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.div>

        <h1 className="font-display text-5xl sm:text-7xl lg:text-9xl leading-[0.9] uppercase max-w-5xl">
          <RevealText text={t.hero.title1} delay={1.9} />
          <br />
          <span style={{ color: "var(--mf-orange)" }}>
            <RevealText text={t.hero.title2} delay={2.2} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.6 }}
          className="mt-6 sm:mt-8 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.85 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center justify-center bg-[#FF6B00] text-black font-bold text-sm tracking-[0.25em] py-4 px-8 btn-glow"
            data-testid="hero-cta-primary"
          >
            {t.hero.ctaPrimary}
          </a>
          <button
            onClick={onWatchStory}
            className="inline-flex items-center justify-center gap-2 border border-white/30 text-white text-sm tracking-[0.25em] py-4 px-8 hover:border-[#FF6B00] hover:text-[#FF6B00] transition"
            data-testid="hero-cta-secondary"
          >
            <Play size={14} fill="currentColor" /> {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <button
          onClick={scrollNext}
          className="mt-16 self-start flex items-center gap-3 text-[10px] tracking-[0.4em] text-zinc-400 hover:text-[#FF6B00] transition"
          data-testid="hero-scroll-indicator"
        >
          <span>{t.hero.scroll}</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          >
            <ArrowDown size={14} />
          </motion.span>
        </button>
      </motion.div>
    </section>
  );
}

function RevealText({ text, delay = 0 }) {
  const words = text.split(" ");
  return (
    <span className="inline-block">
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-3 sm:mr-4">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay: delay + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block"
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
